import type {
  ChannelAsset,
  CoverImage,
  CoverSource,
  EventChannel,
  EventDraftResponse,
  PhotoLayout,
} from '@pulsify/shared/dto'
import { draftStore } from '~/shared/lib/draft-store'
import type { ChatDetails } from './chat-details'
import type { FlowMachine } from './flow-machine'

const DRAFT_VERSION = 1
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000

export interface ChatSnapshot {
  machine: FlowMachine
  pictures: File[]
  draft: EventDraftResponse | null
  subject: { title: string; description: string; startTime: string | null } | null
  variants: CoverImage[]
  coverSource?: CoverSource
  chosen: number
  made: ChannelAsset[]
  photo: File | null
  photoAspect: number | null
  layout: PhotoLayout
  extend: EventChannel[]
  primaryText: string
  secondaryText: string | null
  details: ChatDetails | null
}

interface AiFlowDraft {
  version: typeof DRAFT_VERSION
  savedAt: number
  prompt: string
  pictures: File[]
  withPackage: boolean
  chat: ChatSnapshot | null
}

const draftKey = (businessId: string) => `pulsify-ai-event-flow:${businessId}`

export function aiFlowDraftWorthKeeping(
  draft: Pick<AiFlowDraft, 'prompt' | 'pictures' | 'chat'>,
): boolean {
  return (
    draft.prompt.trim().length > 0 ||
    draft.pictures.length > 0 ||
    (draft.chat?.machine.turns.length ?? 0) > 0
  )
}

export function usableAiFlowDraft(draft: unknown, now = Date.now()): AiFlowDraft | null {
  if (!draft || typeof draft !== 'object') return null
  const stored = draft as AiFlowDraft
  if (stored.version !== DRAFT_VERSION || typeof stored.savedAt !== 'number') return null
  if (now - stored.savedAt > MAX_AGE_MS) return null
  if (typeof stored.prompt !== 'string' || !Array.isArray(stored.pictures)) return null
  return aiFlowDraftWorthKeeping(stored) ? stored : null
}

export async function readAiFlowDraft(businessId: string): Promise<AiFlowDraft | null> {
  if (!businessId) return null
  return usableAiFlowDraft(await draftStore.read(draftKey(businessId)))
}

export async function writeAiFlowDraft(
  businessId: string,
  draft: Omit<AiFlowDraft, 'version' | 'savedAt'>,
): Promise<void> {
  if (!businessId) return
  if (!aiFlowDraftWorthKeeping(draft)) {
    await draftStore.remove(draftKey(businessId))
    return
  }
  await draftStore.write(draftKey(businessId), {
    ...draft,
    version: DRAFT_VERSION,
    savedAt: Date.now(),
  })
}

export async function clearAiFlowDraft(businessId: string): Promise<void> {
  if (!businessId) return
  await draftStore.remove(draftKey(businessId))
}
